import { Alert, Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { PremiseConfig } from "@shared/types/foyer";
import CallDirectoryEditor from "../components/CallDirectoryEditor";
import CallLogViewer from "../components/CallLogViewer";
import { usePermission } from "../auth/access";

/**
 * Care → Video calls (VC-10, VC-25).
 *
 * The tab strip is the page's skeleton, not decoration: this surface is who may
 * call and who rang. Directory (VC-10) is who may call; the Call log (VC-25) is
 * the read-only record of what actually happened. The calling *policy* (VC-12)
 * lives in Config → Video calls, next to the master on/off switch that gates
 * whether this whole section is even shown.
 *
 * Both tabs sit behind the SAME permission. Who may ring a resident's room and
 * who rang it are one safeguarding concern - splitting the rights would mean
 * someone could see the directory without being trusted to read the log of who
 * it connected.
 */
type VideoCallTab = "directory" | "log";

interface Props {
  premise: PremiseConfig;
}

export default function VideoCallsPage({ premise }: Props) {
  const { t } = useTranslation("care");
  const [tab, setTab] = useState<VideoCallTab>("directory");
  const mayView = usePermission("video-calls", "view", {
    premiseId: premise.premiseId,
  });

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5">{t("videoCallsPage.title")}</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 760 }}
        >
          {t("videoCallsPage.subtitle")}
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v: VideoCallTab) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab
          label={t("videoCallsPage.tabs.directory")}
          value="directory"
          data-testid="tab-directory"
        />
        <Tab
          label={t("videoCallsPage.tabs.log")}
          value="log"
          data-testid="tab-log"
        />
      </Tabs>

      {!mayView ? (
        <Alert severity="info" data-testid="call-contacts-denied">
          {t("videoCallsPage.denied")}
        </Alert>
      ) : tab === "directory" ? (
        <CallDirectoryEditor
          premiseId={premise.premiseId}
          premiseTimezone={premise.address?.timezone}
        />
      ) : (
        <CallLogViewer premiseId={premise.premiseId} />
      )}
    </Stack>
  );
}
